/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Industry Types - Business Domain Constants
 *
 * Supabase-compatible enum values for industry classification.
 * Used for:
 * - Organization industry classification
 * - Project categorization
 * - AI prompt selection
 * - Template filtering
 * - Cost database organization
 */

// ============================================================================
// INDUSTRY TYPES - AUTHORITATIVE SOURCE
// ============================================================================

export const INDUSTRIES = {
  CONSTRUCTION: "construction",
  LANDSCAPING: "landscaping",
  PLUMBING: "plumbing",
  ELECTRICAL: "electrical",
  HVAC: "hvac",
  ROOFING: "roofing",
  PAINTING: "painting",
  CLEANING: "cleaning",
  MAINTENANCE: "maintenance",
  RENOVATION: "renovation",
  DEMOLITION: "demolition",
  INSPECTION: "inspection",
  CONSULTING: "consulting",
  OTHER: "other",
} as const;

export type Industry = (typeof INDUSTRIES)[keyof typeof INDUSTRIES];

// ============================================================================
// DISPLAY NAMES - DERIVED FROM INDUSTRIES
// ============================================================================

export const INDUSTRY_DISPLAY_NAMES: Record<Industry, string> = {
  [INDUSTRIES.CONSTRUCTION]: "Construction",
  [INDUSTRIES.LANDSCAPING]: "Landscaping",
  [INDUSTRIES.PLUMBING]: "Plumbing",
  [INDUSTRIES.ELECTRICAL]: "Electrical",
  [INDUSTRIES.HVAC]: "HVAC",
  [INDUSTRIES.ROOFING]: "Roofing",
  [INDUSTRIES.PAINTING]: "Painting",
  [INDUSTRIES.CLEANING]: "Cleaning",
  [INDUSTRIES.MAINTENANCE]: "Maintenance",
  [INDUSTRIES.RENOVATION]: "Renovation",
  [INDUSTRIES.DEMOLITION]: "Demolition",
  [INDUSTRIES.INSPECTION]: "Inspection",
  [INDUSTRIES.CONSULTING]: "Consulting",
  [INDUSTRIES.OTHER]: "Other",
} as const;

// ============================================================================
// VALIDATION & UTILITIES
// ============================================================================

export const INDUSTRY_VALUES = Object.values(INDUSTRIES);

/**
 * Validates if a string is a valid industry value
 */
export const isValidIndustry = (value: string): value is Industry =>
  INDUSTRY_VALUES.includes(value as Industry);

/**
 * Gets the display name for an industry
 */
export function getIndustryDisplayName(industry: string): string {
  return (
    INDUSTRY_DISPLAY_NAMES[industry as keyof typeof INDUSTRY_DISPLAY_NAMES] ||
    industry
  );
}

/**
 * Gets all industries as an array of {value, label} pairs
 */
export const getIndustryOptions = (): Array<{
  value: Industry;
  label: string;
}> =>
  INDUSTRY_VALUES.map((value) => ({
    value,
    label: INDUSTRY_DISPLAY_NAMES[value],
  }));
